import { Decimal } from '@snap-types/types/decimal';

/**
 * The minimum shape required to check quantity ordering. Any quote price
 * structure carrying an identifier and a quantity satisfies it.
 *
 * A null quantity means "not entered yet", which editor state carries while a
 * line is being filled in. Pairs involving one are skipped rather than treated
 * as a violation.
 */
export interface QuotePriceQuantity {
  uuid: string;
  quantity: Decimal | null;
}

export interface QuotePriceQuantityViolation {
  index: number;
  uuid: string;
  quantity: Decimal;
  previousUuid: string;
  previousQuantity: Decimal;
}

/**
 * Quote price quantities must strictly increase across a quote line.
 *
 * Visual enforces uniqueness on (line number, quantity). Strict increase across
 * neighbors guarantees that: if every adjacent pair increases then the whole
 * sequence increases, so all quantities are distinct. Comparing neighbors is
 * sufficient — a non-adjacent collision cannot exist without an adjacent pair
 * also failing.
 *
 * Quote prices must be supplied in display order. Every offending pair is
 * returned so callers can report all of them at once rather than one per
 * attempt.
 */
export function validateQuotePriceQuantities(
  quotePrices: readonly QuotePriceQuantity[],
): QuotePriceQuantityViolation[] {
  const violations: QuotePriceQuantityViolation[] = [];

  let previous: QuotePriceQuantity | null = null;

  for (const [index, current] of quotePrices.entries()) {
    const previousQuantity = previous?.quantity ?? null;
    const currentQuantity = current.quantity;

    if (
      previous !== null &&
      previousQuantity !== null &&
      currentQuantity !== null &&
      !currentQuantity.greaterThan(previousQuantity)
    ) {
      violations.push({
        index,
        uuid: current.uuid,
        quantity: currentQuantity,
        previousUuid: previous.uuid,
        previousQuantity,
      });
    }

    previous = current;
  }

  return violations;
}

export function hasValidQuotePriceQuantities(
  quotePrices: readonly QuotePriceQuantity[],
): boolean {
  return validateQuotePriceQuantities(quotePrices).length === 0;
}

/**
 * Renders violations for error messages and log metadata.
 */
export function describeQuotePriceQuantityViolations(
  violations: readonly QuotePriceQuantityViolation[],
): string {
  return violations
    .map(
      (violation) =>
        `quantity ${violation.quantity.toString()} must be greater than the preceding quantity ${violation.previousQuantity.toString()}`,
    )
    .join('; ');
}
